import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, TrendingUp } from 'lucide-react';
import Navbar from '../components/Navbar';
import api from '../api/axios';
import { safeFormat } from '../lib/utils';

const HolidayTracker = ({ user, onLogout }) => {
  const [holidays, setHolidays] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [leaves, setLeaves] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [holidaysRes, attendanceRes, leavesRes] = await Promise.all([
        api.get('/holidays'),
        api.get('/attendance/my-records'),
        api.get('/leaves/my-leaves'),
      ]);
      setHolidays(holidaysRes.data);
      setAttendanceRecords(attendanceRes.data);
      setLeaves(leavesRes.data);
    } catch (error) {
      console.error('Failed to fetch data');
    }
  };

  const getHolidayStats = () => {
    const totalHolidays = 9; // Fixed total
    let workedOnHolidays = 0;
    let takenHolidays = 0;

    // Get user's registration date (just the date part, ignore time)
    let userCreatedAt = new Date('2026-01-01');
    if (user?.created_at) {
      const createdDate = new Date(user.created_at);
      // Set to start of day for fair comparison
      userCreatedAt = new Date(createdDate.getFullYear(), createdDate.getMonth(), createdDate.getDate());
    }

    // Check each holiday date
    holidays.forEach(holiday => {
      const holidayDate = holiday.date;
      // Skip holidays with a missing/malformed date instead of crashing
      if (typeof holidayDate !== 'string' || !holidayDate) {
        return;
      }
      // Parse the date string properly (YYYY-MM-DD format)
      const parts = holidayDate.split('-').map(Number);
      if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
        return;
      }
      const [year, month, day] = parts;
      const holidayDateObj = new Date(year, month - 1, day); // month is 0-indexed
      
      // Skip holidays that occurred before the employee was registered
      if (holidayDateObj < userCreatedAt) {
        return;
      }
      
      // Check if worked on this holiday (has attendance record)
      const workedOnDay = attendanceRecords.find(record => record.date === holidayDate);
      
      // Check if took leave on this holiday
      const tookLeave = leaves.find(leave => {
        const start = new Date(leave.start_date);
        const end = new Date(leave.end_date);
        return leave.status === 'approved' && holidayDateObj >= start && holidayDateObj <= end;
      });

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (workedOnDay) {
        workedOnHolidays++;
      } else if (!tookLeave && holidayDateObj < today) {
        // Holiday has passed and employee neither worked nor took leave
        takenHolidays++;
      }
    });

    return {
      total: totalHolidays,
      worked: workedOnHolidays,
      taken: takenHolidays,
      remaining: totalHolidays - takenHolidays
    };
  };

  const holidayStats = getHolidayStats();
  const approvedLeaves = leaves.filter((l) => l.status === 'approved');
  const pendingLeaves = leaves.filter((l) => l.status === 'pending');

  const isWorkedOnHoliday = (holidayDate) => {
    return attendanceRecords.some(record => record.date === holidayDate);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar user={user} onLogout={onLogout} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="mb-8">
            <h1 className="text-4xl font-bold font-heading tracking-tight mb-2">
              Holiday Tracker
            </h1>
            <p className="text-lg text-muted-foreground">Track your leave balance and history</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-muted-foreground">Total Holidays</h3>
                <Calendar className="h-5 w-5 text-accent" />
              </div>
              <p className="text-4xl font-bold" data-testid="tracker-total-holidays">
                {holidayStats.total}
              </p>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-muted-foreground">Worked (Claimable)</h3>
                <TrendingUp className="h-5 w-5 text-warning" />
              </div>
              <p className="text-4xl font-bold text-warning" data-testid="tracker-worked-holidays">
                {holidayStats.worked}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Can claim compensatory off</p>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-muted-foreground">Taken Off</h3>
                <Calendar className="h-5 w-5 text-success" />
              </div>
              <p className="text-4xl font-bold text-success" data-testid="tracker-taken-holidays">
                {holidayStats.taken}
              </p>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-muted-foreground">Remaining</h3>
                <Calendar className="h-5 w-5 text-accent" />
              </div>
              <p className="text-4xl font-bold" data-testid="tracker-remaining-holidays">
                {holidayStats.remaining}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-6">
            <h2 className="text-xl font-semibold font-heading tracking-tight mb-4">
              All Holidays
            </h2>
            <div className="space-y-3" data-testid="all-holidays-list">
              {holidays.length > 0 ? (
                holidays.map((holiday) => {
                  const worked = isWorkedOnHoliday(holiday.date);
                  return (
                    <div
                      key={holiday.holiday_id}
                      className={`border-l-4 pl-4 py-2 ${
                        worked ? 'border-warning bg-warning/5' : 'border-accent'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold">{holiday.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {safeFormat(holiday.date, 'MMM d, yyyy')}
                          </p>
                          {holiday.description && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {holiday.description}
                            </p>
                          )}
                        </div>
                        {worked && (
                          <span className="inline-flex items-center rounded-full bg-warning/10 px-2.5 py-0.5 text-xs font-semibold text-warning">
                            Worked - Claimable
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-center py-4 text-muted-foreground">No holidays configured</p>
              )}
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default HolidayTracker;